import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, FileSpreadsheet, LoaderCircle, RotateCcw, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { apiRequest } from "@/lib/queryClient";
import {
  buildMeasurementTargets, compatibleMeasurementUnits, matchMeasurementRow, parseMeasurementFile,
  type MeasurementRow, type MeasurementSelection, type MeasurementTarget, type MeasurementWorkbook,
} from "@/lib/measurement-import";
import type { LVSektion } from "@/components/project/leistungsverzeichnis-kalkulator";
import type { CalculationTemplate, CalculationTemplateSummary } from "@shared/calculation-template";
import type { MeasurementArchiveInput } from "@shared/measurement-archive";

interface MeasurementImportDialogProps {
  sections: LVSektion[];
  projectId?: number;
  onApply: (selection: MeasurementSelection[], archive?: MeasurementArchiveInput) => void | Promise<void>;
}

const number = new Intl.NumberFormat("de-AT", { maximumFractionDigits: 6 });

function rowIsValid(row: MeasurementRow) {
  return row.quantity !== null && Number.isFinite(row.quantity) && row.quantity >= 0 && row.issues.length === 0;
}

export default function MeasurementImportDialog({ sections, projectId, onApply }: MeasurementImportDialogProps) {
  const [open, setOpen] = useState(false);
  const [workbook, setWorkbook] = useState<MeasurementWorkbook | null>(null);
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [sheet, setSheet] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [manualTargets, setManualTargets] = useState<Record<string, string>>({});
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState("");
  const [applyError, setApplyError] = useState("");
  const [reading, setReading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const readToken = useRef(0);
  const selectionContextRef = useRef("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: templates = [], isLoading: listLoading, isError: listError, refetch } =
    useQuery<CalculationTemplateSummary[]>({
      queryKey: ["/api/calculation-templates"],
      enabled: open,
      staleTime: 0,
    });
  const { data: template, isLoading: detailLoading, isFetching: detailFetching, isError: detailError } =
    useQuery<CalculationTemplate>({
      queryKey: ["/api/calculation-templates", activeId],
      enabled: open && !!activeId,
      staleTime: 0,
      queryFn: async () => (await apiRequest("GET", `/api/calculation-templates/${activeId}`)).json(),
    });

  const usableTemplate = template?.id === activeId ? template : undefined;
  const targets = useMemo(
    () => buildMeasurementTargets(sections, usableTemplate),
    [sections, usableTemplate],
  );
  const activeTable = workbook?.tables.find((table) => table.sheet === sheet) ?? null;
  const targetIdForRow = (row: MeasurementRow) =>
    Object.prototype.hasOwnProperty.call(manualTargets, row.id)
      ? manualTargets[row.id]
      : matchMeasurementRow(row, targets).targetId;

  const resetSelection = () => {
    setManualTargets({});
    setSelectedRows(new Set());
    setAcknowledged(false);
  };

  const targetSignature = targets.map((target) =>
    [target.id, target.code, target.name, target.unit, target.section, target.ep, target.existing, target.existingQty].join(":"),
  ).join("|");
  const selectionContext = [
    open, workbook?.fileName ?? "", sheet, activeId ?? "",
    usableTemplate?.id ?? "", usableTemplate?.revision ?? "", usableTemplate?.updatedAt ?? "", targetSignature,
  ].join("::");

  useEffect(() => {
    if (!open) {
      selectionContextRef.current = "";
      return;
    }
    if (!activeTable || selectionContextRef.current === selectionContext) return;
    selectionContextRef.current = selectionContext;
    setManualTargets({});
    setAcknowledged(false);
    setApplyError("");
    const defaults = new Set<string>();
    for (const row of activeTable.rows) {
      const matched = matchMeasurementRow(row, targets);
      const target = targets.find((item) => item.id === matched.targetId);
      if (target && rowIsValid(row) && compatibleMeasurementUnits(row.unit, target.unit) &&
        row.quantity !== null && (row.quantity > 0 || target.existing)) defaults.add(row.id);
    }
    setSelectedRows(defaults);
    // Context changes (table, template, or offer targets) start fresh; local selection edits never reset.
  }, [selectionContext, activeTable, targets, open]);

  useEffect(() => {
    if (!open) {
      readToken.current += 1;
      setReading(false);
    }
  }, [open]);

  useEffect(() => {
    if (activeId && detailFetching) setAcknowledged(false);
  }, [activeId, detailFetching]);

  const mappedTarget = (row: MeasurementRow): MeasurementTarget | undefined => {
    const selectedId = targetIdForRow(row);
    return selectedId ? targets.find((target) => target.id === selectedId) : undefined;
  };
  const selected = activeTable?.rows.filter((row) => selectedRows.has(row.id)) ?? [];
  const notesRequireAck = selected.some((row) => !!row.note.trim());
  const ackRequired = (workbook?.warnings.length ?? 0) > 0 || notesRequireAck;
  const transferReady = !!activeTable && !reading && !applying && !listLoading &&
    (!activeId || (!!usableTemplate && !detailLoading && !detailFetching && !detailError));
  const readyRows = selected.filter((row) => {
    const target = mappedTarget(row);
    return rowIsValid(row) && !!target && compatibleMeasurementUnits(row.unit, target.unit);
  });
  const selectedTargetCounts = new Map<string, number>();
  for (const row of readyRows) {
    const target = mappedTarget(row);
    if (target) selectedTargetCounts.set(target.id, (selectedTargetCounts.get(target.id) ?? 0) + 1);
  }
  const duplicateTargetIds = Array.from(selectedTargetCounts.entries()).filter(([, count]) => count > 1).map(([id]) => id);
  const duplicateTargets = duplicateTargetIds.map((id) => targets.find((target) => target.id === id)).filter(Boolean);
  const hasDuplicateDestinations = duplicateTargetIds.length > 0;
  const unresolvedCount = activeTable?.rows.filter((row) => {
    const target = mappedTarget(row);
    return !rowIsValid(row) || !target || !compatibleMeasurementUnits(row.unit, target.unit);
  }).length ?? 0;
  const skippedCount = (activeTable?.rows.length ?? 0) - readyRows.length;
  const existingCount = readyRows.filter((row) => mappedTarget(row)?.existing).length;
  const newCount = readyRows.length - existingCount;

  const loadFile = async (file?: File) => {
    if (!file) return;
    const token = ++readToken.current;
    setReading(false);
    setError("");
    setWorkbook(null);
    setSourceFile(null);
    setSheet("");
    setActiveId(null);
    setApplyError("");
    resetSelection();
    const extension = file.name.split(".").pop()?.toLocaleLowerCase("de");
    if (!extension || !["xlsx", "xls", "csv", "pdf"].includes(extension)) {
      setError("Bitte eine Excel-Datei (.xlsx, .xls), CSV-Datei (.csv) oder PDF-Maßaufstellung (.pdf) auswählen.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("Die Datei ist größer als 8 MB. Bitte eine kleinere Aufmaßdatei auswählen.");
      return;
    }
    setReading(true);
    try {
      const parsed = await parseMeasurementFile(file);
      if (readToken.current !== token) return;
      setWorkbook(parsed);
      setSourceFile(file);
      const preferredTable = parsed.tables.find((table) => /summary|zusammenfassung|zusammenstellung|übersicht|uebersicht/i.test(table.sheet))
        ?? parsed.tables[0];
      setSheet(preferredTable?.sheet ?? "");
      if (parsed.tables.length === 0) setError("In der Datei wurden keine Tabellen mit Aufmaßzeilen gefunden.");
    } catch (cause) {
      if (readToken.current !== token) return;
      setError(cause instanceof Error ? cause.message : "Die Aufmaßdatei konnte nicht gelesen werden.");
    } finally {
      if (readToken.current === token) setReading(false);
    }
  };

  const changeSheet = (next: string) => {
    setSheet(next);
    resetSelection();
  };

  const chooseTemplate = (id: string) => {
    setActiveId(id || null);
    resetSelection();
  };

  const setRowTarget = (row: MeasurementRow, targetId: string) => {
    const target = targets.find((item) => item.id === targetId);
    setManualTargets((current) => {
      return { ...current, [row.id]: targetId };
    });
    setAcknowledged(false);
    setApplyError("");
    setSelectedRows((current) => {
      const next = new Set(current);
      const canTransfer = !!target && rowIsValid(row) && compatibleMeasurementUnits(row.unit, target.unit) &&
        row.quantity !== null && (row.quantity > 0 || target.existing);
      if (canTransfer && row.quantity! > 0) next.add(row.id);
      else if (!canTransfer || !targetId) next.delete(row.id);
      return next;
    });
  };

  const toggleRow = (id: string, checked: boolean) => {
    setSelectedRows((current) => {
      const next = new Set(current);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
    setAcknowledged(false);
    setApplyError("");
  };

  const apply = async () => {
    if (!transferReady || !readyRows.length || hasDuplicateDestinations || (ackRequired && !acknowledged)) return;
    const selections: MeasurementSelection[] = readyRows.map((row) => ({
      row,
      target: mappedTarget(row)!,
      fileName: workbook!.fileName,
      sheet: activeTable!.sheet,
    }));
    setApplying(true);
    try {
      if (projectId) {
        if (!sourceFile) throw new Error("Bitte die Originaldatei erneut auswählen.");
        const data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(",")[1]);
          reader.onerror = () => reject(new Error("Die Originaldatei konnte nicht gelesen werden."));
          reader.readAsDataURL(sourceFile);
        });
        const importedIds = new Set(readyRows.map((row) => row.id));
        const archive: MeasurementArchiveInput = {
          fileName: sourceFile.name, data, sheet: activeTable!.sheet, confirmed: true,
          rows: activeTable!.rows.map((row) => {
            const target = mappedTarget(row);
            const imported = importedIds.has(row.id);
            return {
              rowNumber: row.rowNumber, sourcePage: row.sourcePage, code: row.code, name: row.name, unit: row.unit,
              quantity: row.quantity, note: row.note, issues: row.issues,
              status: imported ? "imported" : "skipped",
              targetId: imported ? target!.id : undefined,
              targetName: imported ? target!.name : undefined,
              reason: imported ? (target!.existing ? "Vorhandene Menge ersetzt." : "Neue Position übernommen.")
                : row.issues.length ? row.issues.join(" ")
                : !target ? "Kein eindeutiges Ziel zugeordnet."
                : !compatibleMeasurementUnits(row.unit, target.unit) ? "Einheiten sind nicht kompatibel."
                : "Nicht zur Übernahme ausgewählt.",
            };
          }),
          confirmedAssumptions: acknowledged ? Array.from(new Set([
            ...workbook!.warnings,
            ...readyRows.filter((row) => row.note.trim()).map((row) => `${activeTable!.sheet}, Zeile ${row.rowNumber}: ${row.note}`),
          ])) : [],
        };
        await onApply(selections, archive);
      } else {
        await onApply(selections);
      }
      setApplyError("");
      setConfirmOpen(false);
      setOpen(false);
    } catch (cause) {
      setConfirmOpen(false);
      setApplyError(cause instanceof Error ? cause.message : "Die Aufmaßmengen konnten nicht übernommen werden.");
    } finally {
      setApplying(false);
    }
  };

  return <>
    <Dialog open={open} onOpenChange={(next) => {
      if (applying) return;
      setOpen(next);
      if (!next) { setConfirmOpen(false); setError(""); setApplyError(""); setActiveId(null); resetSelection(); }
    }}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline"><FileSpreadsheet className="mr-2 h-4 w-4" />Maßaufstellung importieren</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-5xl">
        <fieldset disabled={applying} className="contents">
        <DialogHeader>
          <DialogTitle>Aufmaß in das Angebot übernehmen</DialogTitle>
          <DialogDescription>Tabellenzeilen eindeutig zuordnen und Mengen bewusst ins sichtbare Leistungsverzeichnis übertragen.</DialogDescription>
        </DialogHeader>

        {error && <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription className="flex flex-wrap items-center justify-between gap-2">{error}
          <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()}><RotateCcw className="mr-1 h-3.5 w-3.5" />Erneut versuchen</Button>
        </AlertDescription></Alert>}
        {applyError && <Alert variant="destructive" role="alert"><AlertTriangle className="h-4 w-4" /><AlertDescription>{applyError}</AlertDescription></Alert>}

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_260px]">
          <section className="min-w-0 space-y-4">
            <div className="rounded-lg border border-dashed border-bauwerk-teal/40 bg-bauwerk-mint/15 p-4">
              <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv,.pdf" className="sr-only" aria-label="Aufmaßdatei auswählen"
                onChange={(event) => { void loadFile(event.target.files?.[0]); event.currentTarget.value = ""; }} />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-bauwerk-mint text-bauwerk-teal"><Upload className="h-5 w-5" /></span>
                  <div className="min-w-0"><p className="text-sm font-semibold">Aufmaßdatei wählen</p>
                    <p className="text-xs text-muted-foreground">XLSX, XLS, CSV oder PDF · maximal 8 MB</p>
                    <p className="text-xs text-muted-foreground">PDF: beschriftete Mengentabellen, maximal 40 Seiten. Keine Scans oder Vermessung aus Bauplänen.</p></div>
                </div>
                <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={reading}>
                  {reading ? <><LoaderCircle className="mr-2 h-4 w-4 animate-spin" />Wird gelesen</> : "Datei auswählen"}
                </Button>
              </div>
              {workbook && <p className="mt-3 truncate border-t pt-3 text-xs text-muted-foreground"><FileSpreadsheet className="mr-1.5 inline h-3.5 w-3.5" />{workbook.fileName}</p>}
            </div>

            {reading && <div className="space-y-2" aria-label="Datei wird gelesen"><Skeleton className="h-10" /><Skeleton className="h-20" /><Skeleton className="h-20" /></div>}

            {workbook && !reading && workbook.tables.length > 0 && <>
              {workbook.tables.length > 1 && <label className="block space-y-1.5 text-sm font-medium">Tabelle / Tabellenblatt / PDF-Seite
                <select value={sheet} onChange={(event) => changeSheet(event.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                  {workbook.tables.map((table) => <option key={table.sheet} value={table.sheet}>{table.sheet}</option>)}
                </select>
              </label>}
              {!!workbook.warnings.length && <Alert className="border-amber-500/40 bg-amber-50/60 text-amber-950">
                <AlertTriangle className="h-4 w-4" /><AlertDescription><strong>Dateihinweise</strong>
                  <ul className="mt-1 list-disc space-y-1 pl-4">{workbook.warnings.map((warning, index) => <li key={`${index}-${warning}`}>{warning}</li>)}</ul>
                </AlertDescription>
              </Alert>}
              {hasDuplicateDestinations && <Alert variant="destructive" role="alert">
                <AlertTriangle className="h-4 w-4" /><AlertDescription>
                  Mehrere ausgewählte Quellzeilen sind demselben Ziel zugeordnet
                  {duplicateTargets.length ? `: ${duplicateTargets.map((target) => target!.name).join(", ")}` : ""}.
                  Bitte jede Zielposition nur einmal auswählen oder die Zuordnung ändern. Mengen werden nicht automatisch addiert.
                </AlertDescription>
              </Alert>}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div><h3 className="text-sm font-semibold">Zeilen prüfen</h3><p className="text-xs text-muted-foreground">Nicht eindeutig erkannte oder unzulässige Zeilen bleiben abgewählt.</p></div>
                <div className="flex gap-2"><Badge variant="outline">{readyRows.length} ausgewählt</Badge><Badge variant="secondary">{unresolvedCount} ungeklärt</Badge></div>
              </div>
              <div className="max-h-[43dvh] overflow-auto rounded-md border">
                <table className="w-full min-w-[760px] border-collapse text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-muted/95 text-xs text-muted-foreground">
                    <tr><th className="w-10 px-3 py-2">Nehmen</th><th className="px-3 py-2">Quelle</th><th className="px-3 py-2">Menge</th><th className="px-3 py-2">Zielposition im Angebot</th></tr>
                  </thead>
                  <tbody className="divide-y">
                    {(activeTable?.rows ?? []).map((row) => {
                      const target = mappedTarget(row);
                      const valid = rowIsValid(row);
                      const unitValid = !!target && compatibleMeasurementUnits(row.unit, target.unit);
                      const canSelect = valid && unitValid && (row.quantity! > 0 || !!target?.existing);
                      const reason = !valid
                        ? row.issues.join(" · ") || (row.quantity === null ? "Menge fehlt oder ist nicht lesbar." : "Negative Mengen sind gesperrt.")
                        : target && !unitValid ? `Einheit passt nicht: ${row.unit || "ohne Einheit"} → ${target.unit}.` : "";
                      return <tr key={row.id} className={!canSelect ? "bg-muted/20" : "hover:bg-bauwerk-mint/10"}>
                        <td className="px-3 py-3 align-top"><input type="checkbox" aria-label={`Zeile ${row.rowNumber} übernehmen`} checked={canSelect && selectedRows.has(row.id)} disabled={!canSelect}
                          onChange={(event) => toggleRow(row.id, event.target.checked)} className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]" /></td>
                        <td className="max-w-[280px] px-3 py-3 align-top">
                          <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-[11px] text-muted-foreground">{row.sourcePage ? `PDF-Seite ${row.sourcePage} · ` : ""}Z. {row.rowNumber}</span>{row.code && <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{row.code}</code>}<span className="font-medium">{row.name || "Ohne Bezeichnung"}</span></div>
                          {row.note && <p className="mt-1 rounded-sm border-l-2 border-amber-500/60 bg-amber-50/60 px-2 py-1 text-xs text-amber-950"><strong>Quellnotiz:</strong> {row.note}</p>}
                          {reason && <p className="mt-1 text-xs text-destructive">{reason}</p>}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 align-top"><span className={row.quantity === 0 ? "font-semibold text-amber-800" : ""}>{row.quantity === null ? "—" : number.format(row.quantity)}</span>
                          <span className="ml-1 text-xs text-muted-foreground">{row.unit || "ohne Einheit"}</span>{row.quantity === 0 && <span className="mt-1 block text-[10px] text-amber-800">Nullmenge</span>}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <select aria-label={`Ziel für Zeile ${row.rowNumber}`} value={targetIdForRow(row) ?? ""}
                            onChange={(event) => setRowTarget(row, event.target.value)}
                            className="h-9 w-full min-w-[250px] rounded-md border border-input bg-background px-2 text-xs">
                            <option value="">Nicht zugeordnet · {matchMeasurementRow(row, targets).reason || "Ziel wählen"}</option>
                            {targets.map((item) => <option key={item.id} value={item.id} disabled={!compatibleMeasurementUnits(row.unit, item.unit)}>
                              {item.code ? `${item.code} · ` : ""}{item.name} — {item.unit} · {item.section}{item.existing ? ` · Bestand ${number.format(item.existingQty ?? 0)}` : " · neue K7-Position"}
                            </option>)}
                          </select>
                          {target && unitValid && <p className="mt-1 text-[11px] text-muted-foreground">{target.existing ? `Vorhandene Menge ${number.format(target.existingQty ?? 0)} wird ersetzt; EP bleibt erhalten.` : `Neue K7-Position · ${new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(target.ep)} / ${target.unit}`}</p>}
                        </td>
                      </tr>;
                    })}
                  </tbody>
                </table>
                {!activeTable?.rows.length && <p className="p-8 text-center text-sm text-muted-foreground">Dieses Tabellenblatt enthält keine Aufmaßzeilen.</p>}
              </div>
              <div className="rounded-md bg-muted/50 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
                {skippedCount} Zeile{skippedCount === 1 ? "" : "n"} werden nicht übertragen. Mengen ersetzen bestehende Angebotsmengen – sie werden nicht addiert. Vorhandene EPs bleiben unverändert.
              </div>
            </>}
            {!workbook && !reading && !error && <div className="rounded-lg border border-dashed p-8 text-center">
              <FileSpreadsheet className="mx-auto h-8 w-8 text-muted-foreground/70" /><p className="mt-3 text-sm font-medium">Noch keine Aufmaßdatei geladen</p>
              <p className="mt-1 text-xs text-muted-foreground">Die Mengen werden erst nach Prüfung und ausdrücklicher Bestätigung übernommen.</p>
            </div>}
            {workbook && !reading && workbook.tables.length === 0 && !error && <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">Keine auswertbaren Tabellen oder Aufmaßzeilen gefunden. Bitte eine andere Datei auswählen.</div>}
          </section>

          <aside className="space-y-3">
            <div className="rounded-lg border p-3">
              <h3 className="text-sm font-semibold">Neue Positionen aus K7</h3>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Optional. Nur Positionen der gespeicherten Vorlage können für noch nicht vorhandene LV-Ziele angeboten werden.</p>
              <select value={activeId ?? ""} onChange={(event) => chooseTemplate(event.target.value)} className="mt-3 h-10 w-full rounded-md border border-input bg-background px-2 text-xs" aria-label="Kalkulationsvorlage">
                <option value="">Keine Vorlage</option>
                {templates.map((item) => <option key={item.id} value={item.id}>{item.name} · Rev. {item.revision}</option>)}
              </select>
              {listLoading && <div className="mt-3 space-y-2"><Skeleton className="h-5" /><Skeleton className="h-5" /></div>}
              {listError && <div className="mt-2 text-xs text-destructive">Vorlagen konnten nicht geladen werden. <button type="button" className="underline" onClick={() => void refetch()}>Erneut laden</button></div>}
              {activeId && (detailLoading || detailFetching) && <p className="mt-2 text-xs text-muted-foreground">Vorlage wird geladen; Übernahme ist bis dahin gesperrt.</p>}
              {activeId && detailError && <p className="mt-2 text-xs text-destructive">Vorlage nicht verfügbar. Bitte erneut auswählen.</p>}
              {usableTemplate && <p className="mt-2 text-xs text-muted-foreground">{usableTemplate.positions.length} Vorlagenpositionen · Preise werden als aktueller Snapshot verwendet. Die Vorlage selbst bleibt unverändert.</p>}
            </div>
            {ackRequired && <label className="flex cursor-pointer items-start gap-2 rounded-md border border-amber-500/40 bg-amber-50/60 p-3 text-xs leading-relaxed text-amber-950">
              <input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-amber-700" />
              <span>Ich habe die Dateihinweise und relevanten Quellnotizen geprüft und bestätige die Verwendung dieser Aufmaßwerte.</span>
            </label>}
            <div className="rounded-md border bg-bauwerk-mint/15 p-3 text-xs">
              <p className="font-semibold">Übernahmeübersicht</p>
              <div className="mt-2 space-y-1 text-muted-foreground">
                <p>{readyRows.length} ausgewählte Mengen</p><p>{existingCount} bestehende · {newCount} neue K7-Ziele</p>
                <p>{unresolvedCount} ungeklärte / ungültige · {skippedCount} übersprungen</p>
              </div>
            </div>
          </aside>
        </div>

        <DialogFooter className="flex-col items-stretch gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
          {confirmOpen ? <>
            <div className="min-w-0 flex-1 rounded-md bg-amber-50/70 p-3 text-xs leading-relaxed text-amber-950">
              <p className="font-semibold">Bitte endgültig bestätigen</p>
              <p className="mt-1">{readyRows.length} Zeilen aus „{workbook?.fileName}“, Blatt „{activeTable?.sheet}“: {existingCount} vorhandene Mengen werden ersetzt, {newCount} K7-Positionen neu eingesetzt. Preise vorhandener Positionen bleiben erhalten. {skippedCount} Zeilen bleiben übersprungen.</p>
              {projectId && <p className="mt-1">Mit der Übernahme werden die vollständige Originaldatei einschließlich weiterer Tabellenblätter und die Importübersicht geschützt in diesem Projekt aufbewahrt. Frühere Importe bleiben erhalten.</p>}
            </div>
            <Button type="button" variant="outline" onClick={() => setConfirmOpen(false)}>Weiter prüfen</Button>
            <Button type="button" disabled={!transferReady || !readyRows.length || hasDuplicateDestinations || (ackRequired && !acknowledged)}
              onClick={() => { void apply(); }}>{applying ? "Wird übernommen …" : "Jetzt Mengen übernehmen"}</Button>
          </> : <>
            <p className="text-xs leading-relaxed text-muted-foreground">Es wird nichts beim Datei-Upload verändert. Erst die finale Bestätigung überträgt die ausgewählten Mengen.</p>
            <Button type="button" disabled={!transferReady || !readyRows.length || hasDuplicateDestinations || (ackRequired && !acknowledged)}
              onClick={() => setConfirmOpen(true)}>Auswahl prüfen · {readyRows.length}</Button>
          </>}
        </DialogFooter>
        </fieldset>
      </DialogContent>
    </Dialog>
  </>;
}
